// src/pages/CompanyAdminUser/CompanyAdminUserForm.jsx
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import Swal from "sweetalert2";
import AlertMessage from "../Common/AlertMessage";

import { getCompanies } from "../../services/companyService";
import {
  createSecurityUser,
  getSecurityUserById,
  updateSecurityUser,
} from "../../services/securityUserService";
import extractApiErrors from "../../utils/extractApiErrors";
import { Container, Card, Form, InputGroup } from "react-bootstrap";
import SelectEmpresa from "../Common/SelectEmpresa";
import { Lock } from "lucide-react";

import { SecurityUserType } from "../../constants/SecurityUserType";

const COMPANY_ADMIN_ROLE_NAME = "company_admin";

// Regex para nombres con Unicode + tildes, espacios, apóstrofes y guiones
const nameRegex = /^[\p{L}\p{M}'\- ]+$/u;

const CompanyAdminUserForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const validationSchema = Yup.object().shape({
    firstName: Yup.string()
      .transform((v) => v?.trim())
      .required("El nombre es obligatorio")
      .min(2, "Debe tener al menos 2 caracteres")
      .max(100, "No debe superar los 100 caracteres")
      .matches(
        nameRegex,
        "El nombre solo puede contener letras, espacios, apóstrofes (') y guiones (-)"
      ),
    lastName: Yup.string()
      .transform((v) => v?.trim())
      .required("Los apellidos son obligatorios")
      .min(2, "Debe tener al menos 2 caracteres")
      .max(100, "No debe superar los 100 caracteres")
      .matches(
        nameRegex,
        "Los apellidos solo pueden contener letras, espacios, apóstrofes (') y guiones (-)"
      ),
    email: Yup.string()
      .transform((v) => v?.trim())
      .email("Correo inválido")
      .required("El correo electrónico es obligatorio"),
    companyId: Yup.string()
      .required("La empresa es obligatoria")
      .test("guid", "El ID de la empresa no es válido", (v) =>
        /^[0-9a-fA-F-]{36}$/.test(v || "")
      ),
    password: !isEdit
      ? Yup.string()
          .required("La contraseña es obligatoria")
          .min(8, "Debe tener al menos 8 caracteres")
          .matches(/[A-Z]/, "Debe contener al menos una mayúscula")
          .matches(/[a-z]/, "Debe contener al menos una minúscula")
          .matches(/[0-9]/, "Debe contener al menos un número")
          .matches(
            /[!@#$%^&*(),.?":{}|<>]/,
            "Debe contener al menos un carácter especial"
          )
      : Yup.string().nullable(),
    confirmPassword: !isEdit
      ? Yup.string()
          .required("Confirma tu contraseña")
          .oneOf([Yup.ref("password")], "La confirmación no coincide")
      : Yup.string().nullable(),
  });

  const [companies, setCompanies] = useState([]);
  const [errors, setErrors] = useState([]);
  const [successMessage, setSuccessMessage] = useState(null);

  const formik = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
      email: "",
      companyId: "",
      password: "",
      confirmPassword: "",
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values, { setSubmitting }) => {
      setErrors([]);
      setSuccessMessage(null);

      try {
        let response;

        if (isEdit) {
          // Update: enviamos firstName/lastName y dejamos roles como estén si aquí no se manejan
          response = await updateSecurityUser({
            id,
            firstName: values.firstName.trim(),
            lastName: values.lastName.trim(),
            email: values.email.trim(),
            isActive: true,
            roleIds: [], // no se manipulan roles aquí
          });
        } else {
          // Create: DTO actualizado con firstName/lastName
          response = await createSecurityUser({
            firstName: values.firstName.trim(),
            lastName: values.lastName.trim(),
            email: values.email.trim(),
            password: values.password,
            confirmPassword: values.confirmPassword,
            companyId: values.companyId,
            companyAdminRoleName: COMPANY_ADMIN_ROLE_NAME, // cuidado con el casing esperado en el backend
            userType: SecurityUserType.CompanyAdmin,
          });
        }

        if (!response?.success) throw { backend: response };

        setSuccessMessage(
          isEdit
            ? "✅ Usuario actualizado correctamente."
            : "✅ Usuario registrado correctamente."
        );
      } catch (error) {
        const parsedErrors = extractApiErrors(error);
        setErrors(parsedErrors);
      } finally {
        setSubmitting(false);
      }
    },
  });

  // Cargar empresas
  useEffect(() => {
    getCompanies(1, 100)
      .then((response) => {
        if (response.success) {
          setCompanies(response.data.items.filter((c) => c.isActive));
        }
      })
      .catch(() => {
        Swal.fire("Error", "No se pudieron cargar las empresas", "error");
      });
  }, []);

  // Cargar usuario en edición
  useEffect(() => {
    if (isEdit) {
      getSecurityUserById(id)
        .then((response) => {
          if (response?.success && response.data) {
            const { firstName, lastName, email, companyId } = response.data;
            formik.setValues({
              firstName: firstName || "",
              lastName: lastName || "",
              email: email || "",
              companyId: companyId || "",
              password: "",
              confirmPassword: "",
            });
          } else {
            throw new Error(
              response?.message || "No se pudo cargar el usuario"
            );
          }
        })
        .catch(() => {
          Swal.fire("Error", "No se pudo cargar el usuario", "error");
        })
        .finally();
    }
  }, [id, isEdit]); // eslint-disable-line react-hooks/exhaustive-deps

  // Navegar tras éxito
  useEffect(() => {
    if (successMessage) {
      const timeout = setTimeout(() => {
        navigate("/dashboard/company-admin-user");
      }, 2000);
      return () => clearTimeout(timeout);
    }
  }, [successMessage, navigate]);

  return (
    <Container className="mt-4">
      <Card className="p-4 novian-form">
        <Card.Header>
          <Card.Title as="h3" className="text-dark">
            {isEdit
              ? "Editar Usuario Administrador de Empresa"
              : "Crear Usuario Administrador de Empresa"}
          </Card.Title>
        </Card.Header>
        <Card.Body>
          <Form noValidate onSubmit={formik.handleSubmit}>
            {/* Nombre */}
            <Form.Group className="mb-3" controlId="firstName">
              <Form.Label>Nombre *</Form.Label>
              <InputGroup hasValidation>
                <Form.Control
                  type="text"
                  name="firstName"
                  value={formik.values.firstName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  isInvalid={
                    formik.touched.firstName && !!formik.errors.firstName
                  }
                />
                <Form.Control.Feedback type="invalid">
                  {formik.errors.firstName}
                </Form.Control.Feedback>
              </InputGroup>
            </Form.Group>

            {/* Apellidos */}
            <Form.Group className="mb-3" controlId="lastName">
              <Form.Label>Apellidos *</Form.Label>
              <InputGroup hasValidation>
                <Form.Control
                  type="text"
                  name="lastName"
                  value={formik.values.lastName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  isInvalid={
                    formik.touched.lastName && !!formik.errors.lastName
                  }
                />
                <Form.Control.Feedback type="invalid">
                  {formik.errors.lastName}
                </Form.Control.Feedback>
              </InputGroup>
            </Form.Group>

            {/* Email */}
            <Form.Group className="mb-3" controlId="email">
              <Form.Label>Correo electrónico *</Form.Label>
              <InputGroup hasValidation>
                <Form.Control
                  type="email"
                  name="email"
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  isInvalid={formik.touched.email && !!formik.errors.email}
                />
                <Form.Control.Feedback type="invalid">
                  {formik.errors.email}
                </Form.Control.Feedback>
              </InputGroup>
            </Form.Group>

            {/* Password (solo en creación) */}
            {!isEdit && (
              <>
                <Form.Group className="mb-3" controlId="password">
                  <Form.Label>Contraseña temporal *</Form.Label>
                  <InputGroup hasValidation>
                    <Form.Control
                      type="password"
                      name="password"
                      value={formik.values.password}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      autoComplete="new-password"
                      isInvalid={
                        formik.touched.password && !!formik.errors.password
                      }
                    />
                    <Form.Control.Feedback type="invalid">
                      {formik.errors.password}
                    </Form.Control.Feedback>
                  </InputGroup>
                </Form.Group>

                <Form.Group className="mb-3" controlId="confirmPassword">
                  <Form.Label>Confirmar contraseña *</Form.Label>
                  <InputGroup hasValidation>
                    <Form.Control
                      type="password"
                      name="confirmPassword"
                      value={formik.values.confirmPassword}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      autoComplete="new-password"
                      isInvalid={
                        formik.touched.confirmPassword &&
                        !!formik.errors.confirmPassword
                      }
                    />
                    <Form.Control.Feedback type="invalid">
                      {formik.errors.confirmPassword}
                    </Form.Control.Feedback>
                  </InputGroup>
                </Form.Group>
              </>
            )}

            {/* Empresa */}
            <Form.Group className="mb-3" controlId="companyId">
              <Form.Label>Empresa *</Form.Label>
              <div className="d-flex align-items-center gap-2">
                <div className="flex-grow-1">
                  <SelectEmpresa
                    options={companies}
                    value={formik.values.companyId}
                    onChange={(val) => formik.setFieldValue("companyId", val)}
                    onBlur={() => formik.setFieldTouched("companyId", true)}
                    className={
                      formik.touched.companyId && formik.errors.companyId
                        ? "is-invalid"
                        : ""
                    }
                    isDisabled={isEdit}
                  />
                </div>
                {isEdit && (
                  <Lock
                    size={20}
                    className="text-muted"
                    title="No se puede cambiar la empresa en modo edición"
                  />
                )}
              </div>

              {formik.touched.companyId && formik.errors.companyId && (
                <div className="invalid-feedback d-block">
                  {formik.errors.companyId}
                </div>
              )}
            </Form.Group>

            {/* Alertas de backend */}
            {errors.length > 0 && (
              <AlertMessage
                type="error"
                message={
                  <ul className="mb-0">
                    {errors.map((e, i) => (
                      <li key={i}>{e}</li>
                    ))}
                  </ul>
                }
                onClose={() => setErrors([])}
              />
            )}

            {successMessage && (
              <AlertMessage
                type="success"
                message={successMessage}
                onClose={() => setSuccessMessage(null)}
              />
            )}

            {/* Botones */}
            <div className="d-flex justify-content-between">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => navigate("/dashboard/company-admin-user")}
              >
                Volver
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={formik.isSubmitting}
              >
                {formik.isSubmitting
                  ? "Guardando..."
                  : isEdit
                  ? "Actualizar"
                  : "Crear"}
              </button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default CompanyAdminUserForm;
